import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  fetchTopSingles,
  fetchTopDoubles,
  fetchRecentTournaments,
  fetchHomeStats,
  type RankingItem,
  type TournamentProjectionItem,
  type HomeStats,
} from "../utils/homeApi";
import { cleanTournamentName } from "../utils/cleanTournamentName";
import { leagueRoundPathFor } from "./tournament-round-redirect";
import { pluralPL } from "../utils/pluralPL";

function ratingTier(rating: number): string {
  if (rating < 1600) {
    return "low";
  }

  if (rating < 1800) {
    return "medium";
  }

  if (rating < 2000) {
    return "high";
  }

  return "very-high";
}

function winRatePct(player: RankingItem): number {
  const played = player.gamesPlayed ?? 0;
  const won = player.gamesWon ?? 0;

  if (played === 0) {
    return 0;
  }

  return (won / played) * 100;
}

function formatTournamentDate(raw: string | null | undefined): string {
  if (!raw) {
    return "—";
  }
  const d = new Date(raw);

  if (Number.isNaN(d.getTime())) {
    return "—";
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function tournamentTypeLabel(
  t: TournamentProjectionItem,
): "Singiel" | "Debel" | "Mieszany" {
  const hasSingles = (t.singlesMatchCount ?? 0) > 0;
  const hasDoubles = (t.doublesMatchCount ?? 0) > 0;
  if (hasSingles && hasDoubles) {
    return "Mieszany";
  }

  if (hasDoubles) {
    return "Debel";
  }

  return "Singiel";
}

const Home: React.FC = () => {
  const [topPlayers, setTopPlayers] = useState<RankingItem[]>([]);
  const [topTeamPlayers, setTopTeamPlayers] = useState<RankingItem[]>([]);
  const [latestTournaments, setLatestTournaments] = useState<
    TournamentProjectionItem[]
  >([]);
  const [stats, setStats] = useState<HomeStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [singlesRes, doublesRes, tournamentsRes, statsRes] =
          await Promise.all([
            fetchTopSingles(5),
            fetchTopDoubles(5),
            fetchRecentTournaments(10),
            fetchHomeStats(),
          ]);

        setTopPlayers(singlesRes.items);
        setTopTeamPlayers(doublesRes.items);
        setLatestTournaments(tournamentsRes.items);
        setStats(statsRes);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const totalPlayers = stats?.singlesPlayers ?? 0;
  const totalTeamPlayers = stats?.doublesPlayers ?? 0;
  const totalSinglesMatches = stats?.singlesMatches ?? 0;
  const totalTeamMatches = stats?.doublesMatches ?? 0;
  const totalTournaments = stats?.tournaments ?? 0;

  return (
    <div className="home">
      <section className="hero">
        <span className="eyebrow">Polski badminton · sezon 2026</span>
        <h1 className="hero-title">
          <span>Badminton</span>
          <span className="hero-title-accent">Arbiter</span>
        </h1>
        <p className="hero-sub">
          Ranking, turnieje, ligi i wyniki w jednym miejscu — śledź formę,
          statystyki i wiele więcej.
        </p>
        <div className="hero-cta">
          <Link to="/events" className="btn-primary">
            Zobacz turnieje
          </Link>
          <Link to="/players" className="btn-secondary">
            Ranking graczy
          </Link>
        </div>
        <svg
          className="hero-shuttle"
          viewBox="0 0 32 32"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <g transform="rotate(14 16 16)">
            <path
              d="M11 19 L3 5.5 Q16 -1.5 29 5.5 L21 19 Z"
              fill="#fffdf5"
              stroke="var(--court)"
              strokeWidth="2.4"
              strokeLinejoin="round"
            />
            <path
              d="M16 19 V3 M12.6 19 L7.4 5 M19.4 19 L24.6 5"
              fill="none"
              stroke="var(--court)"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path d="M10 19 H22 V24.5 A6 6 0 0 1 10 24.5 Z" fill="var(--court)" />
            <rect x="10" y="19" width="12" height="3.2" fill="#d64545" />
          </g>
        </svg>
      </section>

      {loading && <div className="loading">Wczytywanie danych…</div>}

      {!loading && (
        <>
          <section className="scoreboard">
            <div className="scoreboard-row">
              <div className="scoreboard-cell">
                <span className="scoreboard-label">Gracze</span>
                <span className="scoreboard-value">{totalPlayers}</span>
              </div>
              <div className="scoreboard-cell">
                <span className="scoreboard-label">Drużynowi</span>
                <span className="scoreboard-value">{totalTeamPlayers}</span>
              </div>
              <div className="scoreboard-cell scoreboard-accent">
                <span className="scoreboard-label">Mecze singla</span>
                <span className="scoreboard-value">{totalSinglesMatches}</span>
              </div>
              <div className="scoreboard-cell">
                <span className="scoreboard-label">Mecze drużynowe</span>
                <span className="scoreboard-value">{totalTeamMatches}</span>
              </div>
              <div className="scoreboard-cell">
                <span className="scoreboard-label">Turnieje</span>
                <span className="scoreboard-value">{totalTournaments}</span>
              </div>
            </div>
          </section>

          <section className="section">
            <header className="section-header">
              <span className="eyebrow">Najnowsze</span>
              <h2>Ostatnie turnieje</h2>
              <Link to="/tournaments?view=all" className="section-link">
                Wszystkie wyniki →
              </Link>
            </header>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Turniej</th>
                    <th className="numeric">Mecze</th>
                    <th className="numeric">Gracze</th>
                    <th>Typ</th>
                  </tr>
                </thead>
                <tbody>
                  {latestTournaments.map((t) => {
                    const type = tournamentTypeLabel(t);
                    const cleaned = cleanTournamentName(t.name);
                    const displayName =
                      cleaned && cleaned !== `Tournament #${t.tournamentNumber}`
                        ? cleaned
                        : `Turniej ${t.tournamentNumber}`;
                    const dateStr = formatTournamentDate(t.date);
                    const target =
                      leagueRoundPathFor(t) ?? `/tournament/${t.tournamentNumber}`;
                    return (
                      <tr key={t.tournamentNumber}>
                        <td className="mono">
                          <Link to={target} className="player-link">
                            {dateStr}
                          </Link>
                        </td>
                        <td>
                          <Link to={target} className="player-link">
                            {displayName}
                          </Link>
                        </td>
                        <td className="numeric">{t.matchCount}</td>
                        <td className="numeric">{t.uniquePlayerCount}</td>
                        <td>
                          <span
                            className={`type-tag type-${type.toLowerCase()}`}
                          >
                            {type}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <div className="two-col">
            <section className="section">
              <header className="section-header">
                <span className="eyebrow">Top 5</span>
                <h2>Single</h2>
                <Link to="/players" className="section-link">
                  Pełny ranking →
                </Link>
              </header>
              <div className="rank-list">
                {topPlayers.map((player, index) => (
                  <Link
                    to={`/player/${player.playerId}`}
                    key={player.playerId}
                    className="rank-row"
                  >
                    <span className="rank-pos">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="rank-name">{player.name}</span>
                    <span
                      className={`rating-badge ${ratingTier(player.rating)}`}
                    >
                      {player.rating}
                    </span>
                    <span
                      className="rank-meta"
                      title={`${winRatePct(player).toFixed(1)}% wygranych z ${player.gamesPlayed} ${pluralPL(player.gamesPlayed, 'meczu', 'meczów', 'meczów')}`}
                    >
                      {player.gamesPlayed} {pluralPL(player.gamesPlayed, 'mecz', 'mecze', 'meczów')} ·{" "}
                      {winRatePct(player).toFixed(0)}%
                    </span>
                  </Link>
                ))}
              </div>
            </section>

            <section className="section">
              <header className="section-header">
                <span className="eyebrow">Top 5</span>
                <h2>Debel</h2>
                <Link to="/team-players" className="section-link">
                  Pełny ranking →
                </Link>
              </header>
              <div className="rank-list">
                {topTeamPlayers.map((player, index) => (
                  <Link
                    to={`/player/${player.playerId}?tab=debel`}
                    key={player.playerId}
                    className="rank-row"
                  >
                    <span className="rank-pos">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="rank-name">{player.name}</span>
                    <span
                      className={`rating-badge ${ratingTier(player.rating)}`}
                    >
                      {player.rating}
                    </span>
                    <span
                      className="rank-meta"
                      title={`${winRatePct(player).toFixed(1)}% wygranych z ${player.gamesPlayed} ${pluralPL(player.gamesPlayed, 'meczu', 'meczów', 'meczów')}`}
                    >
                      {player.gamesPlayed} {pluralPL(player.gamesPlayed, 'mecz', 'mecze', 'meczów')} ·{" "}
                      {winRatePct(player).toFixed(0)}%
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default Home;
